import { AlertCircle, CalendarRange, CheckCircle2, Clock3 } from 'lucide-react';
import { Card } from '../../components/ui';
import { cn } from '../../lib/cn';
import { formatMoney, formatPercent } from '../../lib/format';
import type { WeekData } from './api';
import { weekCapacity } from './capacity';
import { weekCompleteness } from './completeness';
import { formatHoursValue, todayIso, type HoursFormat } from './hours';

export interface WeekSummaryProps {
  week: WeekData;
  /** Unsaved hours a running timer is adding to today. */
  runningHours: number;
  /** Whether the row the timer is running on bills — so the split stays honest. */
  runningBillable?: boolean;
  /** Hours being typed into a cell right now, not yet committed. */
  previewDelta?: number;
  /** Whether that in-flight edit belongs to a billable row. */
  previewBillable?: boolean;
  /**
   * Cells whose last save was REJECTED by the server. Their hours are held in
   * the grid but the server never took them, so they are not in any figure
   * here — and this strip has to say so rather than quietly reading like the
   * week is fine.
   */
  unsavedCells?: number;
  hoursFormat: HoursFormat;
  /** Jump the page to a specific day (day view). */
  onOpenDay: (date: string) => void;
}

/**
 * The context a timesheet screen owes a professional: how the week is
 * tracking against a full week, how much of it is billable (the dot in the
 * grid finally gets a legend), and which days are still blank.
 */
export function WeekSummary({
  week,
  runningHours,
  runningBillable = true,
  previewDelta = 0,
  previewBillable = true,
  unsavedCells = 0,
  hoursFormat,
  onOpenDay,
}: WeekSummaryProps) {
  const today = todayIso();
  const total = Math.max(0, Math.round((week.weekTotal + runningHours + previewDelta) * 100) / 100);
  // The running clock DOES know which row it is on, so its seconds land on the
  // correct side of the split rather than quietly dropping out of both.
  const billable = Math.max(
    0,
    Math.round(
      (week.rows.reduce((sum, row) => sum + (row.billable ? row.total : 0), 0) +
        (previewBillable ? previewDelta : 0) +
        (runningBillable ? runningHours : 0)) *
        100,
    ) / 100,
  );
  const internal = Math.max(
    0,
    Math.round(
      (week.rows.reduce((sum, row) => sum + (row.billable ? 0 : row.total), 0) +
        (previewBillable ? 0 : previewDelta) +
        (runningBillable ? 0 : runningHours)) *
        100,
    ) / 100,
  );
  const saved = billable + internal;
  const billableRatio = saved > 0 ? billable / saved : 0;

  /**
   * What the billable half of this week is WORTH.
   *
   * The day view has always printed a per-entry amount; the week grid printed
   * no currency at all, so the same person's same week was a money screen one
   * click away and a stopwatch here. Same rule as the day row and the approvals
   * roster, so the three can never disagree: billable hours, on agreements billed
   * BY THE HOUR (an hourly valuation of fixed-fee work is not billable money),
   * on work that still exists.
   */
  const billableValue =
    Math.round(
      week.rows.reduce(
        (sum, row) =>
          row.agreement.billBy === 'HOURS'
            ? sum +
              row.entries.reduce(
                (rowSum, entry) =>
                  entry.billable && !entry.deletedInSource && entry.billRate != null
                    ? rowSum + entry.hours * entry.billRate
                    : rowSum,
                0,
              )
            : sum,
        0,
      ) * 100,
    ) / 100;
  /** Billable hours whose agreement is not hourly — value comes from the fee. */
  const feeHours =
    Math.round(
      week.rows.reduce(
        (sum, row) => (row.billable && row.agreement.billBy !== 'HOURS' ? sum + row.total : sum),
        0,
      ) * 100,
    ) / 100;
  /**
   * Billable hours the dollar figure actually values — hourly work. None of
   * those, with fee hours present, is the ALL-FEE-WORK week: the money is
   * genuinely $0 and the reason is the whole story, so the card has to say it
   * rather than print nothing at all.
   */
  const hourlyBillableHours =
    Math.round(
      week.rows.reduce(
        (sum, row) => (row.billable && row.agreement.billBy === 'HOURS' ? sum + row.total : sum),
        0,
      ) * 100,
    ) / 100;
  const allFeeWork = feeHours > 0 && hourlyBillableHours === 0;

  // The person's OWN contracted week — a 16h contractor is never measured
  // against 40h. null = no committed capacity, and we say that rather than
  // printing a target nobody agreed to.
  const capacity = weekCapacity(week.person);
  const target = capacity.weekly;
  const remaining =
    target === null ? 0 : Math.max(0, Math.round((target - total) * 100) / 100);
  const over = target === null ? 0 : Math.max(0, Math.round((total - target) * 100) / 100);
  const atCapacity = target !== null && total >= target;

  /**
   * ONE definition of "is this week finished?", shared with the submit dialog
   * (`completeness.ts`). It counts a weekday as a gap when it is BLANK **or**
   * short of the person's contracted day — a presence check called a 36-second
   * timer "every weekday tracked" — and it counts a running clock onto today,
   * so this card cannot say "nothing yet" while seconds tick above it.
   */
  const c = weekCompleteness(week, { extraHours: previewDelta, runningToday: runningHours });
  const notStarted = c.notStarted;
  /** Weekdays BEFORE today with nothing or less than a contracted day. */
  const missed = c.gaps;
  /** Today, still empty — reported as state, not as a problem. */
  const todayOpen = c.todayOpen;

  /**
   * ⚠ A BLANK weekday is a gap; a SHORT one is pace. They must not wear the
   * same amber.
   *
   * "Thursday holds nothing" is a missing timesheet — somebody has to go and
   * find those hours. "Thursday holds 6.50 of 8" is a normal working week that
   * is running a little light, and raising a warning icon over five of those
   * every Friday is precisely how a warning stops being read. So the alert
   * colour keys on blanks only, and short days are listed in neutral grey.
   */
  const hasBlank = c.blank.length > 0;

  /** Gap status in one phrase — the phone summary has no room for a panel. */
  const gapPhrase = notStarted
    ? 'Upcoming week'
    : hasBlank
      ? `${c.blank.length} weekday${c.blank.length > 1 ? 's' : ''} with no time`
      : c.short.length > 0
        ? `${c.short.length} weekday${c.short.length > 1 ? 's' : ''} under a full day`
        : todayOpen
          ? 'Today — nothing yet'
          : 'Every weekday tracked';

  return (
    <Card className="mb-3">
      {/* Rejected hours sit in the grid but never reached the server, so every
          figure below is computed WITHOUT them. A summary that silently reads
          "16.50 tracked · 79% billable" while a cell is amber is telling the
          user the week is fine when it is not. */}
      {unsavedCells > 0 && (
        <p className="flex items-start gap-1.5 rounded-t-xl border-b border-warning/30 bg-warning/10 px-4 py-1.5 text-caption font-semibold text-status-partial">
          <AlertCircle className="h-3.5 w-3.5 shrink-0 translate-y-px" aria-hidden />
          <span>
            Provisional — {unsavedCells} rejected cell{unsavedCells > 1 ? 's are' : ' is'} still in
            the grid and <span className="underline decoration-dotted">not counted</span> below.
            Retry from the amber marker in the cell.
          </span>
        </p>
      )}
      {/* Phone: ONE line, not three stacked panels. At 390px the three-up card
          spent a third of the screen before a single hour of data appeared —
          the same four figures fit on two lines here. */}
      <div className="px-4 py-2.5 sm:hidden">
        <p className="flex flex-wrap items-baseline gap-x-2">
          <span
            className={cn(
              'text-heading-3 font-black num',
              previewDelta !== 0 ? 'text-accent' : 'text-secondary',
            )}
          >
            {formatHoursValue(total, hoursFormat)}
          </span>
          <span className="text-body-sm text-grey-500">
            {target === null ? 'tracked' : `of ${capacity.weeklyLabel}`}
          </span>
          <span className="ml-auto text-body-sm text-grey-500">
            <span className="font-bold text-secondary num">
              {saved > 0 ? formatPercent(billableRatio, { decimals: 0 }) : '—'}
            </span>{' '}
            billable
          </span>
        </p>
        {target !== null && (
          <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-grey-200">
            <div
              className={cn('h-full rounded-full', total >= target ? 'bg-success' : 'bg-accent')}
              style={{ width: `${Math.min(100, (total / target) * 100)}%` }}
            />
          </div>
        )}
        <p className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-caption text-grey-500">
          {hasBlank && !atCapacity ? (
            <AlertCircle className="h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
          ) : missed.length > 0 || todayOpen ? (
            <Clock3 className="h-3.5 w-3.5 shrink-0 text-grey-400" aria-hidden />
          ) : (
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-success" aria-hidden />
          )}
          <span className="font-semibold text-grey-600">{gapPhrase}</span>
          {missed.map((gap) => (
            <button
              key={gap.date}
              type="button"
              onClick={() => onOpenDay(gap.date)}
              className={cn(
                'rounded-md border px-1.5 py-0.5 font-semibold',
                gap.kind === 'blank'
                  ? 'border-warning/40 bg-warning/10 text-status-partial'
                  : 'border-grey-200 bg-grey-50 text-grey-500',
              )}
            >
              {gap.label}
              {gap.kind === 'short' && (
                <span className="ml-1 font-normal num">
                  {formatHoursValue(gap.hours, hoursFormat)}
                </span>
              )}
            </button>
          ))}
        </p>
      </div>

      <div className="hidden divide-grey-100 sm:grid sm:grid-cols-3 sm:divide-x">
      {/* Tracked vs THIS person's contracted week.
          Each panel is label → figure line → meter. The trailing status sits
          on the RIGHT of the figure line rather than on a fourth line of its
          own: this card sits directly above the grid, and every line it takes
          is a timesheet row pushed below the fold. */}
      <div className="px-4 py-2.5">
        <p className="text-caption font-bold uppercase tracking-wider text-grey-500">
          Tracked this week
        </p>
        <p className="mt-0.5 flex flex-wrap items-baseline gap-x-1.5">
          <span
            className={cn(
              'text-heading-3 font-black num',
              previewDelta !== 0 ? 'text-accent' : 'text-secondary',
            )}
          >
            {formatHoursValue(total, hoursFormat)}
          </span>
          {target === null ? (
            <span className="text-body-sm text-grey-500">tracked</span>
          ) : (
            <span
              className="text-body-sm text-grey-500"
              title={`${week.person.name} is contracted for ${capacity.weeklyLabel} a week (${capacity.dailyLabel} a day).`}
            >
              of {capacity.weeklyLabel}
            </span>
          )}
          <span className="ml-auto text-caption text-grey-500 num">
            {target === null ? (
              <span className="font-sans">No weekly target set</span>
            ) : remaining > 0 ? (
              `${formatHoursValue(remaining, hoursFormat)} to go`
            ) : over > 0 ? (
              `${formatHoursValue(over, hoursFormat)} over ${capacity.weeklyLabel}`
            ) : (
              <span className="font-sans">Full week reached</span>
            )}
          </span>
        </p>
        {target === null ? (
          // No committed capacity: an invented bar would be a lie, so the
          // meter's slot carries the reason instead.
          <div className="mt-1.5 h-1.5 w-full rounded-full border border-dashed border-grey-200" />
        ) : (
          <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-grey-200">
            <div
              className={cn('h-full rounded-full', total >= target ? 'bg-success' : 'bg-accent')}
              style={{ width: `${Math.min(100, (total / target) * 100)}%` }}
            />
          </div>
        )}
      </div>

      {/* Billable split — the legend for the green dot in the grid */}
      <div className="px-4 py-2.5">
        <p className="text-caption font-bold uppercase tracking-wider text-grey-500">
          Billable split
        </p>
        <div className="mt-0.5 flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
          <p className="flex items-baseline gap-1.5">
            <span className="text-heading-3 font-black text-secondary num">
              {saved > 0 ? formatPercent(billableRatio, { decimals: 0 }) : '—'}
            </span>
            <span className="text-body-sm text-grey-500">
              {saved > 0 ? 'billable' : 'nothing tracked yet'}
            </span>
            {/* The money the week grid never printed. Whole dollars: this is a
                rollup across a set of entries, not one document's figure. */}
            {billableValue > 0 ? (
              <span
                className="whitespace-nowrap text-body-sm font-bold text-secondary num"
                title={
                  feeHours > 0
                    ? `Billable value of the hourly work in this week. ${formatHoursValue(
                        feeHours,
                        hoursFormat,
                      )} of billable time sits on fee-based agreements and is not valued by the hour.`
                    : 'Billable value of this week — billable hours × their bill rate.'
                }
              >
                · {formatMoney(billableValue, { whole: true })}
              </span>
            ) : (
              /* ⚠ THE ALL-FEE-WORK WEEK. Every billable hour sits on a
                 fee-based agreement, so hours × rate is $0 — and the slot went
                 BLANK, leaving "76% billable · Billable 12.00" one click from a
                 colleague's "89% billable · $2,416", which reads as missing
                 data rather than as a real week of fee work. The word is the
                 roster's own ("Fee work" in the Value column, on the day view,
                 on the phone card) so a reader who learned it there is not
                 taught a second one. Not a figure, so not figure-weight. */
              allFeeWork && (
                <span className="whitespace-nowrap text-body-sm font-semibold text-grey-500">
                  · Fee work
                </span>
              )
            )}
          </p>
          {/* ⚠ THE MONEY MUST NAME WHAT IT LEAVES OUT — INCLUDING WHEN IT
              LEAVES OUT ALL OF IT. The card read "83% billable · $1,973" beside
              a legend saying "Billable 33.50" while 21.50 of those hours were
              fee work worth nothing in that figure — the qualifier lived only
              in a `title`, where a reader comparing two numbers on screen never
              finds it. The all-fee-work week was worse: it dropped the dollars
              AND this sentence, so the card said nothing at all. It takes a
              line of its own (`basis-full`): folded into the figure line it
              wrapped between the "·" and the dollars. */}
          {feeHours > 0 && (
            <p
              className="basis-full text-caption text-grey-500"
              title={
                allFeeWork
                  ? 'Every billable hour in this week sits on a fee-based agreement. Their value comes from the fee, not from hours × rate — so there is no hourly figure to print, and hours × rate here would be money nobody may invoice.'
                  : 'Billable hours on fee-based agreements. Their value comes from the fee, not from hours × rate, so they sit outside the dollar figure.'
              }
            >
              {allFeeWork ? 'All ' : ''}
              <span className="num">{formatHoursValue(feeHours, hoursFormat)}</span>
              {allFeeWork ? ' billable hours are fee work' : ' of those billable hours are fee work'}
              {billableValue > 0 ? ' — outside the $' : ' — valued by the fee, not by hours × rate'}
            </p>
          )}
          <p className="ml-auto flex flex-wrap items-center gap-x-3 gap-y-1 text-caption text-grey-500">
            <span className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-success" aria-hidden />
              Billable{' '}
              <span className="num text-grey-600">{formatHoursValue(billable, hoursFormat)}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-grey-300" aria-hidden />
              Internal{' '}
              <span className="num text-grey-600">{formatHoursValue(internal, hoursFormat)}</span>
            </span>
          </p>
        </div>
        <div className="mt-1.5 flex h-1.5 w-full overflow-hidden rounded-full bg-grey-200">
          <div className="h-full bg-success" style={{ width: `${billableRatio * 100}%` }} />
          <div className="h-full bg-grey-300" style={{ width: `${(1 - billableRatio) * 100}%` }} />
        </div>
      </div>

      {/* Gaps so far */}
      <div className="px-4 py-2.5">
        <p className="text-caption font-bold uppercase tracking-wider text-grey-500">
          Days so far
        </p>
        {notStarted ? (
          <>
            <p className="mt-0.5 flex items-center gap-1.5 text-body-sm font-semibold text-secondary">
              <CalendarRange className="h-4 w-4 shrink-0 text-grey-400" aria-hidden />
              Upcoming week
            </p>
            <p className="mt-1.5 text-caption text-grey-500">
              Nothing to track here yet.
            </p>
          </>
        ) : missed.length === 0 ? (
          <>
            <p className="mt-0.5 flex items-center gap-1.5 text-body-sm font-semibold text-secondary">
              {todayOpen ? (
                <Clock3 className="h-4 w-4 shrink-0 text-grey-400" aria-hidden />
              ) : (
                <CheckCircle2 className="h-4 w-4 shrink-0 text-success" aria-hidden />
              )}
              {todayOpen ? 'Today — nothing yet' : 'Every weekday tracked'}
            </p>
            <p className="mt-1.5 text-caption text-grey-500">
              {todayOpen
                ? 'Every earlier weekday this week is tracked.'
                : 'Nothing missing up to today.'}
            </p>
          </>
        ) : (
          <>
            <p className="mt-0.5 flex items-center gap-1.5 text-body-sm font-semibold text-secondary">
              {/* A part-timer who has already met their contracted week has not
                  "missed" anything — the gaps are listed, not flagged. Nor has
                  someone whose days are all present but a little light: only a
                  BLANK weekday raises the amber. */}
              {atCapacity || !hasBlank ? (
                hasBlank || c.short.length > 0 ? (
                  <Clock3 className="h-4 w-4 shrink-0 text-grey-400" aria-hidden />
                ) : (
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-success" aria-hidden />
                )
              ) : (
                <AlertCircle className="h-4 w-4 shrink-0 text-warning" aria-hidden />
              )}
              {gapPhrase}
            </p>
            {atCapacity ? (
              <p className="mt-0.5 text-caption text-grey-500">
                Week already at {capacity.weeklyLabel}.
              </p>
            ) : (
              !hasBlank &&
              capacity.dailyLabel !== '' && (
                <p className="mt-0.5 text-caption text-grey-500">
                  Every weekday has time; these are under {capacity.dailyLabel}.
                </p>
              )
            )}
            <div className="mt-1.5 flex flex-wrap gap-1">
              {missed.map((gap) => (
                <button
                  key={gap.date}
                  type="button"
                  onClick={() => onOpenDay(gap.date)}
                  title={
                    gap.kind === 'blank'
                      ? `Open ${gap.label} — no time logged`
                      : `Open ${gap.label} — ${formatHoursValue(gap.hours, hoursFormat)} of a ${
                          capacity.dailyLabel
                        } day`
                  }
                  className={cn(
                    'rounded-md border px-1.5 py-0.5 text-caption font-semibold transition-colors',
                    gap.kind === 'blank'
                      ? 'border-warning/40 bg-warning/10 text-status-partial hover:bg-warning/20'
                      : 'border-grey-200 bg-grey-50 text-grey-500 hover:bg-grey-100',
                  )}
                >
                  {gap.label}
                  {gap.kind === 'short' && (
                    <span className="ml-1 font-normal num">
                      {formatHoursValue(gap.hours, hoursFormat)}
                    </span>
                  )}
                </button>
              ))}
              {/* Neutral by design — today is not a gap, it is in progress. */}
              {todayOpen && (
                <button
                  type="button"
                  onClick={() => onOpenDay(today)}
                  title="Open today — nothing logged yet"
                  className="rounded-md border border-grey-200 bg-grey-50 px-1.5 py-0.5 text-caption font-semibold text-grey-500 transition-colors hover:bg-grey-100"
                >
                  Today
                </button>
              )}
            </div>
          </>
        )}
      </div>
      </div>
    </Card>
  );
}
